import type { ColumnDef, TableState } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { centsToFormattedDollars, formatDate, formatPhoneNumber } from '@pesto/utils';
import { MONEY_MULTIPLIER } from '@pesto/utils/constants/pestoConstants';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '../../../components/Button';
import { Typography } from '../../../components/Typography';
import { BooleanTag } from '../components/BooleanTag';
import { getColumnFilterKey, setColumnFilterAndSortKey } from '../utils/helpers';

export type WithChildren<T> = T & { children?: ReactNode[] };

export interface ColumnConfig<T = any> {
  id: string;
  header: string | ((props: { column: any }) => ReactNode);
  accessorFn?: (row: T) => any;
  accessorKey?: keyof T;
  cell?: (row: any) => ReactNode;
  enablePinning?: boolean;
  enableSorting?: boolean;
  size?: number;
  className?: string;
  cellType?: 'text' | 'date' | 'phone' | 'boolean' | 'currency' | 'currencyWithMultiplier';
  dateFormat?: string;
}

export type FilterVariant = 'text' | 'select' | 'multiSelect' | 'radio' | 'boolean';

export interface FilterFieldConfig {
  label: string;
  value: string;
  placeholder?: string;
  options?: any[];
  isCustomCheckboxFilter?: boolean;
  variant?: FilterVariant;
  source?: 'column' | 'query';
}

export interface ListConfigOptions<T = any> {
  columns: ColumnConfig<T>[];
  filterFields?: FilterFieldConfig[];
  initialTableState?: Partial<TableState>;
  showActionColumn?: boolean;
  actionButtonText?: string;
  actionButtonVariant?: 'outline' | 'ghost' | 'link' | 'primary';
  isActionDisabled?: (row: T) => boolean;
  pinnedColumns?: string[];
  defaultSorting?: { id: string; desc: boolean }[];
  columnVisibility?: Record<string, boolean>;
  customCellRenderers?: Record<string, (value: any) => ReactNode>;
}

const defaultCellRenderers = {
  text: (value: string | null | undefined) => (
    <Typography variant={'body'} className={'w-auto text-nowrap'}>
      {value || ''}
    </Typography>
  ),
  date: (value: string | null | undefined) => (
    <Typography variant={'body'} className={'w-auto text-nowrap'}>
      {value ? formatDate(value) : ''}
    </Typography>
  ),
  phone: (value: string | null | undefined) => (
    <Typography variant={'body'} className={'w-auto text-nowrap'}>
      {value ? formatPhoneNumber(value) : ''}
    </Typography>
  ),
  boolean: (value: boolean | null | undefined) => <BooleanTag value={!!value} />,
  currency: (value: number | null | undefined) => (
    <Typography variant={'body'} className={'w-auto text-nowrap'}>
      {value ? centsToFormattedDollars(value) : ''}
    </Typography>
  ),
  currencyWithMultiplier: (value: number | null | undefined) => (
    <Typography variant={'body'} className={'w-auto text-nowrap'}>
      {value ? centsToFormattedDollars(value * MONEY_MULTIPLIER) : ''}
    </Typography>
  ),
};

export const useGenericListConfig = <T = any>(options: ListConfigOptions<T>) => {
  const {
    columns,
    filterFields = [],
    initialTableState = {},
    showActionColumn = false,
    actionButtonText = 'View',
    actionButtonVariant = 'outline',
    isActionDisabled,
    pinnedColumns = [],
    defaultSorting = [{ id: 'insertedAtSortDirection', desc: true }],
    columnVisibility = {},
    customCellRenderers = {},
  } = options;

  const getColumns = (handleRowClicked?: (row: T) => void): ColumnDef<T>[] => {
    const actionColumn: ColumnDef<T> | null = showActionColumn
      ? {
          id: 'view',
          header: 'Action',
          // SAFETY: tables that enable the action column list entities, and every listed entity has an `id`.
          accessorKey: 'id' as keyof T,
          enablePinning: true,
          cell: (row) => (
            <Button
              disabled={isActionDisabled?.(row.cell.row.original)}
              onClick={() => handleRowClicked?.(row.cell.row.original)}
              variant={actionButtonVariant}
            >
              {actionButtonText}
            </Button>
          ),
        }
      : null;

    const dataColumns: ColumnDef<T>[] = columns.map((column) => {
      const { cellType } = column;

      const columnDef: ColumnDef<T> = {
        id: column.id,
        header: column.header,
        enablePinning: column.enablePinning,
        enableSorting: column.enableSorting,
        size: column.size,
        ...(column.accessorFn && { accessorFn: column.accessorFn }),
        ...(column.accessorKey && { accessorKey: column.accessorKey }),
        ...(column.cell && { cell: column.cell }),
        ...(!column.cell &&
          cellType && {
            cell: (row: any) => {
              const value = row.cell.getValue();
              const renderer = customCellRenderers[cellType] ?? defaultCellRenderers[cellType];

              return renderer(value);
            },
          }),
      };

      return columnDef;
    });

    return actionColumn ? [actionColumn, ...dataColumns] : dataColumns;
  };

  const finalInitialTableState: Partial<TableState> = {
    ...(pinnedColumns.length > 0 && { columnPinning: { left: pinnedColumns } }),
    sorting: defaultSorting,
    ...(Object.keys(columnVisibility).length > 0 && { columnVisibility }),
    ...initialTableState,
  };

  return {
    filterFields,
    getColumns,
    initialTableState: finalInitialTableState,
  };
};

export const createColumnConfig = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> => ({
  id,
  header,
  ...options,
});

export const createFilterField = (
  label: string,
  value: string,
  options: Partial<FilterFieldConfig> = {},
): FilterFieldConfig => {
  if (!getColumnFilterKey(value)) {
    throw new Error(`Filter "${label}" has a sort-only column id, so its value could never reach the URL or the query`);
  }

  return { label, value, source: 'column', ...options };
};

export const createQueryFilterField = <TVariables extends Record<string, unknown>>(
  label: string,
  value: Extract<keyof TVariables, string>,
  options: Omit<Partial<FilterFieldConfig>, 'label' | 'value' | 'source'> = {},
): FilterFieldConfig => ({
  label,
  value,
  source: 'query',
  ...options,
});

export const createSortableColumnId = (searchKey: string, sortKey: string) =>
  setColumnFilterAndSortKey(searchKey, sortKey);

export const createTextColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => string | null | undefined,
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> =>
  createColumnConfig(id, header, {
    accessorFn,
    cellType: 'text',
    ...options,
  });

export const createDateColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => string | null | undefined,
  options: Partial<ColumnConfig<T>> & { dateFormat?: string } = {},
): ColumnConfig<T> => {
  const { dateFormat, ...restOptions } = options;

  if (dateFormat) {
    return createColumnConfig(id, header, {
      accessorFn,
      cell: (row: any) => {
        const value = row.cell.getValue();

        return (
          <Typography variant="body" className="w-auto text-nowrap">
            {value ? formatDate(value, dateFormat) : ''}
          </Typography>
        );
      },
      ...restOptions,
    });
  }

  return createColumnConfig(id, header, {
    accessorFn,
    cellType: 'date',
    ...restOptions,
  });
};

export const createPhoneColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => string | null | undefined,
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> =>
  createColumnConfig(id, header, {
    accessorFn,
    cellType: 'phone',
    ...options,
  });

export const createBooleanColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => boolean | null | undefined,
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> =>
  createColumnConfig(id, header, {
    accessorFn,
    cellType: 'boolean',
    ...options,
  });

export const createCurrencyColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => number | null | undefined,
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> =>
  createColumnConfig(id, header, {
    accessorFn,
    cellType: 'currency',
    ...options,
  });

export const createCurrencyWithMultiplierColumn = <T = any>(
  id: string,
  header: string | ((props: { column: any }) => ReactNode),
  accessorFn: (row: T) => number | null | undefined,
  options: Partial<ColumnConfig<T>> = {},
): ColumnConfig<T> =>
  createColumnConfig(id, header, {
    accessorFn,
    cellType: 'currencyWithMultiplier',
    ...options,
  });

export const createExpanderColumn = <T = any>(label: string): ColumnConfig<T> =>
  createColumnConfig('expander', label, {
    cell: ({ row }) => (
      <div style={{ paddingLeft: `${row.depth * 2}rem` }}>
        {row.getCanExpand() ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={row.getToggleExpandedHandler()}
            className="p-1"
            style={{ cursor: 'pointer' }}
          >
            {row.getIsExpanded() ? (
              <>
                <ChevronDown className="h-4 w-4" /> {label}
              </>
            ) : (
              <>
                <ChevronRight className="h-4 w-4" /> {label}
              </>
            )}
          </Button>
        ) : null}
      </div>
    ),
    size: 40,
    enableSorting: false,
  });
